import {
  getConversations,
  getConversationCount,
  getConversationDetail,
  getConversationUser,
  createConversation,
  sendMessage,
  deleteConversation,
} from "./utils";

class HttpDataClient {
  /**
   * Generic request handler with consistent error handling
   * @param {Promise} apiCall - API call to execute
   * @returns {Promise} Formatted response or error
   */
  async #handleRequest(apiCall) {
    try {
      const response = await apiCall;
      return {
        success: true,
        data: response.data.data,
        meta: response.data.meta,
        status: response.status,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message,
        error: error.response?.data || error.message,
        status: error.response?.status || 500,
      };
    }
  }

  /**
   * Get conversations with filters
   * @param {Object} filters
   * @param {string} [filters.filter] - Filter type (e.g., 'read', 'unread')
   * @param {number} [filters.page] - Page number for pagination
   * @param {number} [filters.per_page] - Number of items per page
   * @returns {Promise<any>}
   */
  async getConversations(filters = {}) {
    const finalFilters = {
      page: filters.page || 1,
      per_page: filters.per_page || 12,
      ...(filters.filter && { filter: filters.filter }),
    };

    return this.#handleRequest(getConversations(finalFilters));
  }

  /**
   * Get conversation count
   * @param {Object} config - Request configuration
   * @returns {Promise} Formatted response
   */
  getConversationCount(config = {}) {
    return this.#handleRequest(getConversationCount(config));
  }

  /**
   * Get conversation details
   * @param {string} conversationId - Conversation ID
   * @param {Object} params - Pagination parameters
   * @param {number} [params.page] - Page number
   * @param {number} [params.per_page] - Items per page
   * @returns {Promise<any>}
   */
  async getConversationDetail(conversationId, params = {}) {
    const finalParams = {
      page: params.page || 1,
      per_page: params.per_page || 12,
    };

    return this.#handleRequest(
      getConversationDetail(conversationId, finalParams)
    );
  }

  /**
   * Get conversation user details
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<any>}
   */
  async getConversationUser(conversationId) {
    return this.#handleRequest(getConversationUser(conversationId));
  }

  /**
   * Create a new conversation
   * @param {Object} data - Conversation data
   * @returns {Promise<any>}
   */
  async createConversation(data) {
    return this.#handleRequest(createConversation(data));
  }

  /**
   * Send a message in a conversation
   * @param {string} conversationId - Conversation ID
   * @param {Object} message - Message data
   * @returns {Promise<any>}
   */
  async sendMessage(conversationId, message) {
    return this.#handleRequest(sendMessage(conversationId, message));
  }

  /**
   * Delete a conversation
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<any>}
   */
  async deleteConversation(conversationId) {
    return this.#handleRequest(deleteConversation(conversationId));
  }
}

const Conversation = new HttpDataClient();

export default Conversation;
